/**
 * GM command panel — in-game console triggered by "/" key.
 * Shows a filterable command list above the input. Sends commands
 * to the server via WebSocket and displays results.
 */

import { GameWebSocketClient } from './network_websocket_client';

const GM_PANEL_HEIGHT_PX = 30;
const LIST_ITEM_HEIGHT = 22;
const MAX_VISIBLE_ITEMS = 6;

interface GmCommandDef {
  name: string;
  args: string;
  desc: string;
}

const GM_COMMANDS: GmCommandDef[] = [
  { name: 'addstone',    args: '<数量>', desc: '添加灵石' },
  { name: 'addqi',       args: '<数量>', desc: '添加灵气' },
  { name: 'addherb',     args: '<数量>', desc: '添加草药' },
  { name: 'addpill',     args: '<数量>', desc: '添加丹药' },
  { name: 'addartifact', args: '<数量>', desc: '添加法器' },
  { name: 'addcore',     args: '<数量>', desc: '添加傀儡核心' },
];

export class UiGmCommandPanel {
  private input: HTMLInputElement;
  private panel: HTMLElement;
  private result: HTMLElement;
  private debugLog: HTMLElement;
  private listContainer: HTMLElement;
  private wsClient: GameWebSocketClient | null = null;
  private resultTimer: ReturnType<typeof setTimeout> | null = null;
  private filteredCommands: GmCommandDef[] = [];

  constructor() {
    this.input = document.getElementById('gm-input') as HTMLInputElement;
    this.panel = document.getElementById('gm-panel') as HTMLElement;
    this.result = document.getElementById('gm-result') as HTMLElement;
    this.debugLog = document.getElementById('debug-log') as HTMLElement;
    this.listContainer = document.getElementById('gm-list') as HTMLElement;

    // Enter → send command; Escape → close panel
    this.input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.sendCommand();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        this.hide();
      }
    });

    // Input typing → filter command list
    this.input.addEventListener('input', () => this.updateList());
  }

  /** Register the WebSocket client (must be called after connection setup). */
  setWsClient(client: GameWebSocketClient): void {
    this.wsClient = client;
  }

  get visible(): boolean {
    return this.panel.style.display !== 'none';
  }

  toggle(): void {
    if (this.visible) {
      this.hide();
    } else {
      this.show();
    }
  }

  show(): void {
    this.panel.style.display = 'block';
    this.input.focus();
    this.input.value = '';
    this.updateList();
    // Shift debug-log up
    const listH = this.listHeight();
    this.debugLog.style.bottom = `calc(var(--safe-bottom) + var(--mobile-bar-h) + ${GM_PANEL_HEIGHT_PX + listH}px)`;
  }

  hide(): void {
    this.panel.style.display = 'none';
    this.input.value = '';
    this.listContainer.innerHTML = '';
    this.debugLog.style.bottom = '';
  }

  /** Called by game_client_world_state when a GmResult message arrives. */
  showResult(message: string): void {
    this.result.textContent = message;
    if (this.resultTimer) clearTimeout(this.resultTimer);
    this.resultTimer = setTimeout(() => {
      this.result.textContent = '';
    }, 3000);
  }

  // ── command list filtering ──

  private updateList(): void {
    const query = this.input.value.trim().toLowerCase();
    if (!query) {
      // Show all commands
      this.filteredCommands = [...GM_COMMANDS];
    } else {
      this.filteredCommands = GM_COMMANDS.filter(c =>
        c.name.indexOf(query) !== -1 || c.desc.indexOf(query) !== -1,
      );
    }
    this.renderList();

    // Adjust panel height for list
    const listH = this.listHeight();
    this.listContainer.style.height = `${listH}px`;
    this.debugLog.style.bottom = `calc(var(--safe-bottom) + var(--mobile-bar-h) + ${GM_PANEL_HEIGHT_PX + listH}px)`;
  }

  private listHeight(): number {
    return Math.min(this.filteredCommands.length, MAX_VISIBLE_ITEMS) * LIST_ITEM_HEIGHT;
  }

  private renderList(): void {
    this.listContainer.innerHTML = '';
    const visible = this.filteredCommands.slice(0, MAX_VISIBLE_ITEMS);
    for (const cmd of visible) {
      const item = document.createElement('div');
      item.style.cssText = `
        height:${LIST_ITEM_HEIGHT}px;line-height:${LIST_ITEM_HEIGHT}px;
        color:#aaa;font-family:monospace;font-size:11px;
        padding:0 8px;cursor:pointer;white-space:nowrap;
      `;
      item.innerHTML = `<span style="color:#ff4444;">/${cmd.name}</span> <span style="color:#888;">${cmd.args}</span> <span style="color:#666;">— ${cmd.desc}</span>`;

      // Click to fill input
      item.addEventListener('mousedown', (e) => {
        e.preventDefault();
        this.input.value = `/${cmd.name} `;
        this.input.focus();
        this.updateList();
      });

      // Hover highlight
      item.addEventListener('mouseenter', () => { item.style.background = '#222'; });
      item.addEventListener('mouseleave', () => { item.style.background = ''; });

      this.listContainer.appendChild(item);
    }
  }

  // ── send ──

  private sendCommand(): void {
    const cmd = this.input.value.trim();
    if (!cmd) return;
    if (!this.wsClient) {
      this.showResult('(未连接服务器)');
      return;
    }
    this.wsClient.send({ type: 'GmCommand', command: cmd });
    this.input.value = '';
    this.updateList();
    this.input.focus();
  }
}
